/**
 * Where the candidates are — in THREE readings, not fourteen bars (Theresa, Sep 29 2026).
 *
 *   New opportunities   everything before an assessment: leads, outreach, applicants, shortlist
 *   In assessment       every round: the test, first, personal, case and final interviews
 *   Offers out          a tile of its own — see `OfferTile`
 *
 * ⚠️ WHY IT IS NOT ONE LIST ANY MORE. Fourteen stage bars answered "which stage exactly", which is
 * a question the role cards below already answer per pipeline. The one this section exists for is
 * where the MASS sits — is the top of the funnel full, is assessment clogged, is anything close to
 * closing — and three totals answer it at a glance where fourteen rows did not.
 *
 * ⚠️ THE GROUPS ARE A JUDGEMENT AND LIVE IN `utils/openPositionsFunnel.ts`, with the stage names
 * they match on. Same split as the radar's traffic lights: the warehouse ships stages and counts,
 * and how FINN reads its own funnel is client-side, testable without a network, and expected to
 * change. A stage matching nothing lands in a fourth group that is RENDERED — Lever's stage names
 * are edited in Lever, and a chart claiming to account for every active candidate must not quietly
 * lose one.
 *
 * ⚠️ AUTOMATION STAGES ARE DROPPED, and the TOTAL IS REBUILT without them. "To be Archived" holds
 * candidates a Lever automation is about to act on rather than candidates anybody is working
 * (Theresa, Sep 29 2026). The header therefore counts what the three charts account for, not the
 * payload's own `totalCandidates` — a header over charts that do not add up to it is the mistake
 * `PortfolioReconciliation` exists to prevent, one section up.
 *
 * ⚠️ IT LIVES ON TRACKING → DELIVERY since Sep 29 2026 (Theresa), not on the Pipeline Radar. It had
 * been on the radar since the Open Positions page was deleted, on the argument that the radar says
 * which ROLES are stuck and this says which STAGE the candidates are in. What it actually is, is a
 * live read of how the trimester is being delivered — the question one tab over — and on the radar
 * it never honoured that tab's cluster filter, so it sat inside a filtered page describing a
 * different population every time somebody used the control above it.
 *
 * ⚠️ IT IS STILL A DIFFERENT POPULATION FROM THE FIGURES ABOVE IT, and the section says so rather
 * than letting two counts on one page look comparable when they are not. Tracking counts
 * hiring-plan rows for one trimester; this counts every active candidate on every published Lever
 * posting, whatever trimester their role belongs to.
 *
 * ⚠️ IT DEGRADES ON ITS OWN (`useCandidateFunnel`). A failure here renders one line and leaves the
 * delivery figures working, and a Tracking outage leaves this section up — the rule has to hold
 * both ways or it is a coincidence. The offer tile degrades once more on top of that: it is a
 * second query behind the same route.
 */
import { AlertCircle, Loader2 } from 'lucide-react';
import type { FunnelStage, OpenPositionsFunnel } from '../../../types/openPositions';
import { readFunnel, type StageGroup } from '../../../utils/openPositionsFunnel';
import { Card } from '../rolePrioritisation/prioritisationUi';
import { Section } from './trackingUi';
import { OfferTile } from './OfferTile';

/**
 * One hue per group, light to dark within it.
 *
 * ⚠️ Still a SEQUENCE, not a status scale — nothing in these charts is a verdict, and reusing the
 * radar's green/amber/red two sections up would put two colour systems on one page. What changed is
 * that the sequence now restarts per group, so the group a bar belongs to is readable without the
 * heading: blues run the top of the funnel, teals run assessment.
 */
const GROUP_PALETTE: Record<StageGroup, string[]> = {
  new: ['#BFDBFE', '#93C5FD', '#60A5FA', '#3B82F6', '#2563EB', '#1D4ED8'],
  assessment: ['#A7F3D0', '#6EE7B7', '#34D399', '#10B981', '#059669', '#047857'],
  offer: ['#C084FC'],
  other: ['#CBD5E1'],
};

const GROUP_TITLE: Record<StageGroup, string> = {
  new: 'New opportunities',
  assessment: 'In assessment',
  offer: 'At the offer',
  other: 'Elsewhere',
};

const GROUP_NOTE: Record<StageGroup, string> = {
  new: 'everything before an assessment',
  assessment: 'the test and every interview round',
  offer: 'see the tile',
  other: 'stages in neither group — check the names in Lever',
};

export function StageFunnel({
  data,
  error,
  loading,
}: {
  data: OpenPositionsFunnel | null;
  error: string | null;
  loading: boolean;
}) {
  /*
   * ⚠️ The previous read is KEPT in state across a reload, so `data` alone is not "these figures are
   * current". A failed re-read would otherwise put last read's counts in the header above a card
   * that says it could not be read — the one thing a header on a failing section must not do.
   */
  const current = !loading && !error ? data : null;
  const reading = current ? readFunnel(current.stages) : null;
  // The offer tile stands on its own, so its stages are not drawn as bars beside it.
  const charted = reading ? reading.groups.filter((g) => g.group !== 'offer') : [];

  return (
    <Section
      title="Where the candidates are"
      aside={
        current && reading
          ? `${reading.total} active across ${current.openPositionCount} open postings`
          : undefined
      }
    >
      <div className="mt-3">
      {loading ? (
        <Card className="flex items-center gap-2 p-5 text-sm text-iron">
          <Loader2 className="h-4 w-4 animate-spin" />
          Reading the pipeline…
        </Card>
      ) : error || !data ? (
        <Card className="flex items-start gap-2 p-5 text-sm text-iron">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
          <span>
            The candidate funnel could not be read{error ? `: ${error}` : ''}. The roles below are
            unaffected — they come from a different source.
          </span>
        </Card>
      ) : data.stages.length === 0 ? (
        <Card className="p-5 text-sm text-iron">No active candidates in the pipeline.</Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_320px]">
            <div className="flex flex-col gap-3">
              {charted.map((g) => (
                <GroupCard
                  key={g.group}
                  group={g.group}
                  stages={g.stages}
                  total={g.total}
                  ofTotal={reading?.total ?? 0}
                />
              ))}
            </div>
            <OfferTile pulse={data.offers} />
          </div>

          {current && reading ? (
            <p className="mt-3 text-[11px] leading-4 text-steel">
              Every published Lever posting and its active candidates — a wider population than the
              figures above, which are this trimester&apos;s hiring-plan rows.
              {reading.automationCandidates > 0
                ? ` Automation stages are left out (${reading.automationCandidates} ${
                    reading.automationCandidates === 1 ? 'candidate' : 'candidates'
                  }), so this total is below Lever's own.`
                : ''}{' '}
              The offer tile counts differently again: offers awaiting an answer on a role that is
              still open, which is not the same as candidates sitting in the offer stage.
            </p>
          ) : null}
        </>
      )}
      </div>
    </Section>
  );
}

function GroupCard({
  group,
  stages,
  total,
  ofTotal,
}: {
  group: StageGroup;
  stages: FunnelStage[];
  total: number;
  ofTotal: number;
}) {
  const palette = GROUP_PALETTE[group];
  /*
   * Scaled WITHIN the group, so the stages inside one chart compare with each other. Across groups
   * the totals in the headers are what compare — a shared scale would flatten every assessment
   * round against a 114-candidate applicant stage and show nothing.
   */
  const max = stages.reduce((m, s) => Math.max(m, s.candidates), 0);

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-sm font-semibold text-black">
          {GROUP_TITLE[group]}{' '}
          <span className="ml-1 text-xs font-normal text-steel">{GROUP_NOTE[group]}</span>
        </h3>
        <span className="text-sm tabular-nums text-asphalt">
          <b className="text-lg font-semibold text-black">{total}</b>
          <span className="ml-1.5 text-xs text-steel">
            {ofTotal > 0 ? `${Math.round((total / ofTotal) * 100)}% of all active` : ''}
          </span>
        </span>
      </div>

      <div className="space-y-1.5">
        {stages.map((stage, i) => {
          const pct = total > 0 ? (stage.candidates / total) * 100 : 0;
          /*
           * The 2% floor is for a stage holding ONE candidate, not for one holding none — a bar
           * drawn for zero reads as a stage with somebody in it.
           */
          const width =
            stage.candidates > 0 && max > 0 ? Math.max((stage.candidates / max) * 100, 2) : 0;
          return (
            <div key={stage.name} className="flex items-center gap-3 text-sm">
              <div className="w-40 shrink-0 truncate text-asphalt" title={stage.name}>
                {stage.name}
              </div>
              <div className="relative h-5 flex-1 rounded bg-cotton">
                <div
                  className="h-5 rounded"
                  style={{
                    width: `${width}%`,
                    backgroundColor: palette[i % palette.length],
                  }}
                />
              </div>
              <div className="w-20 shrink-0 text-right tabular-nums text-black">
                {stage.candidates}
                <span className="ml-1 text-xs text-steel">{pct.toFixed(0)}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
